/* ===================================================================
   SmsTimelineExplainer -- "How texting gets switched on."

   WHY THIS EXISTS (Steve, 2026-07-29, verbatim): "I wanna set the
   expectation accurately. I don't wanna undersell it, and then it looks
   like we're idiots because it's taking a month and a half when it's
   really... when we're telling them it's gonna take two weeks."

   FACT BASE -- do not soften, do not embellish. Every claim below is
   traceable to a document, not to a sales instinct:

   - We run MODEL A (reports/2026-07-28_twilio-a2p-isv-provisioning-plan.md,
     sections 2 + 8): ONE already-APPROVED Standard brand + ONE approved
     CUSTOMER_CARE campaign + one Messaging Service, with a DEDICATED local
     number bought per client and attached to that shared service. Per-client
     carrier vetting under Model A: NONE. No new brand, no new campaign, no
     carrier clock. Number provisioning is an API call.
     => The multi-week wait people remember is MODEL B (a secondary brand per
        client, incl. the sole-proprietor OTP). We are NOT doing Model B. So
        the copy below must NOT invent a two-to-six-week carrier wait. It
        would be a lie in the client's favour today and a broken promise the
        day we ever do move a client to Model B.

   - The REAL gating step the client will feel is the CONSUMER DOUBLE OPT-IN
     (reports/2026-07-28_model-a-compliance-review.md, Condition 1, and the
     shipped engine/compliance/sms_consent.py state machine): our registered
     campaign promises carriers that the first touch is a "Reply YES to
     confirm" message and that NO review request goes out until that YES is
     logged. A business owner cannot consent on their customer's behalf.
     => "SMS is on" does NOT mean "we can text your list today." The list is
        built one confirmed customer at a time. Say so plainly.

   - Quiet hours are enforced in the RECIPIENT's local timezone, 8am-9pm
     (provisioning/area_code.py: _LOCAL_START_HOUR=8, _LOCAL_END_HOUR=21,
     consulted by integrations/twilio_sms.py). "Nothing between 9pm and 8am
     their time" is exactly what the code does.

   - Every message names BOTH the client business AND Rhody Reviews
     (Model A compliance review, Condition 4).

   HONESTY GUARD: review-request SMS does not transmit yet on the live
   backend (REQUEST_SMS_LIVE is off, TWILIO_USE_TEST=1, and the sms_consent
   migration is proposed-not-applied). So this component promises NO date
   and NO "your texting is live now." It says email works today and texting
   switches on per account when that account's number is live. Standing rule:
   never advertise what we do not do.

   DOUBLES AS AN ONBOARDING SCRIPT (Steve's second audience): the four steps
   are written in the order an onboarder should say them out loud on a demo
   or setup call, in plain spoken sentences. Whoever runs onboarding (Steve
   now, a VA later) can open this on the call and read down it. The matching
   internal runbook (the parts a client should never see) lives at
   otb_package/reports/2026-07-29_sms-onboarding-script-internal.md -- it is
   deliberately NOT in this component.
   =================================================================== */
import '../styles/sms-timeline.css';

const ChatIcon = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2}>
    <path d="M21 11.5a8.4 8.4 0 0 1-8.5 8.5 8.5 8.5 0 0 1-3.7-.8L3 21l1.9-5.7a8.5 8.5 0 1 1 16.1-3.8z" />
  </svg>
);
const CaretIcon = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.6} className="smst__caret">
    <path d="M6 9l6 6 6-6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

export function SmsTimelineExplainer({
  /** Open on first paint. Use on the SMS panel (they came asking); leave
      closed at the card step so a ready buyer is never walled off by a block
      of text he did not ask for. */
  defaultOpen = false,
}: {
  defaultOpen?: boolean;
}) {
  return (
    <details className="smst" open={defaultOpen}>
      <summary className="smst__sum">
        <span className="smst__ico" aria-hidden="true">{ChatIcon}</span>
        <span className="smst__sumtxt">
          How texting gets switched on (and what actually takes time)
        </span>
        {CaretIcon}
      </summary>

      <div className="smst__body">
        <p className="smst__lede">
          Worth two minutes now so nothing surprises you later. Here is the honest version.
        </p>

        <div className="smst__steps">
          <div className="smst__step">
            <span className="smst__n" aria-hidden="true">1</span>
            <div>
              <div className="smst__h">Your own local number. Minutes, not weeks.</div>
              <p className="smst__p">
                Rhody Reviews already holds the approved carrier registration that business
                texting requires. Your business does not have to go through its own carrier
                review, so there is no multi-week paperwork wait on your side. You get a
                dedicated number in your own area code, assigned to your account only. It is
                not a shared pool.
              </p>
            </div>
          </div>

          <div className="smst__step">
            <span className="smst__n" aria-hidden="true">2</span>
            <div>
              <div className="smst__h">Every customer opts in before we can text them.</div>
              <p className="smst__p">
                Federal rules do not let a business give texting consent on its customer's
                behalf. So the first message any customer gets is one line asking them to
                reply YES. Only after that YES is on file does Rhody Reviews send them a
                review request. Anyone who does not reply is simply never texted again.
              </p>
            </div>
          </div>

          <div className="smst__step">
            <span className="smst__n" aria-hidden="true">3</span>
            <div>
              <div className="smst__h">This is the part that takes real time.</div>
              <p className="smst__p">
                On day one your texting list is empty, and it fills one customer at a time as
                people confirm, at the pace of your normal job flow. Expect a build over
                weeks, not a switch that flips. If anyone tells you a brand-new number can
                text your whole customer list tomorrow, they are handing you a compliance
                problem, not a feature.
              </p>
            </div>
          </div>

          <div className="smst__step">
            <span className="smst__n" aria-hidden="true">4</span>
            <div>
              <div className="smst__h">Email works from day one. Start there.</div>
              <p className="smst__p">
                Email needs none of the above. Every customer with an email address can get a
                review request right away, so your reviews start moving immediately while the
                texting list builds underneath it.
              </p>
            </div>
          </div>
        </div>

        <p className="smst__note">
          <b>Always on, both channels.</b> Quiet hours are enforced in your customer's own
          local time, so nothing goes out between 9pm and 8am where they are. STOP is honored
          immediately and permanently. Every message names your business and Rhody Reviews, so
          your customer always knows who is texting them.
        </p>

        <p className="smst__note">
          <b>Where you are right now.</b> Your account sends by email today. Texting switches
          on account by account as each dedicated number goes live, and the Requests screen
          shows SMS as available the moment yours does. Authorizing texting below costs you
          nothing and does not start anything on its own.
        </p>
      </div>
    </details>
  );
}
